import { AbacusStateActionStatus } from "@abacus/ows-state-client";
import { Environment } from "@abacus/common";

export function GET_ACCOUNT_CONTRACTS_BY_ACCOUNT_IDS_AND_CONTRACT_IDS(
  db: string,
  env: Environment,
  accountIds: number[],
  contractIds: number[],
): string {
  if (accountIds.length < 1) {
    throw new RangeError("Must filter by at least 1 account ID");
  }
  if (contractIds.length < 1) {
    throw new RangeError("Must filter by at least 1 contract ID");
  }
  return `
        SELECT
            account_id AS "accountId",
            contract_id AS "contractId",
            created_at AS "created",
            created_by AS "createdBy",
            account_contract_id AS "id",
            is_primary_for_calc AS "isCalcPrimary",
            last_modified AS "lastModified",
            last_modified_by AS "lastModifiedBy"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.account_contract 
        WHERE 
            account_id IN (${accountIds.join(",")})
            AND contract_id IN (${contractIds.join(",")})
    ;`;
}

export function GET_ACCOUNT_IDS_BY_ACCOUNT_IDS(
  db: string,
  env: Environment,
  accountIds: number[],
): string {
  if (accountIds.length < 1) {
    throw new RangeError("Must filter by at least 1 account ID");
  }
  return `
        SELECT 
            account_id AS "id"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.account 
        WHERE 
            account_id IN (${accountIds.join(",")})
    ;`;
}

export function GET_ACCOUNT_PAYMENT_ENTITY_IDS_BY_ACCOUNT_IDS(
  db: string,
  env: Environment,
  accountIds: number[],
): string {
  if (accountIds.length < 1) {
    throw new RangeError("Must filter by at least 1 account ID");
  }
  return `
        SELECT 
            account_id AS "accountId",
            payment_entity_id AS "paymentEntityId"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.account_payment_term
        WHERE 
            payment_entity_id IS NOT NULL
            AND account_id IN (${accountIds.join(",")})
    ;`;
}

export function GET_ADJUSTMENT_TYPES(db: string, env: Environment): string {
  return `
        SELECT 
            reference_adjustment_type_id AS "id",
            type_name AS "name",
            oa_category_name AS "oaCategoryName"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.reference_adjustment_type
    ;`;
}

export function GET_CONTRACT_IDS_BY_CONTRACT_IDS(
  db: string,
  env: Environment,
  contractIds: number[],
): string {
  if (contractIds.length < 1) {
    throw new RangeError("Must filter by at least 1 contract ID");
  }
  return `
        SELECT 
            contract_id AS "id"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.contract 
        WHERE 
            contract_id IN (${contractIds.join(",")})
    ;`;
}

export function GET_CONTRACT_TERM_ATTACHMENTS_BY_TERM_TYPES_AND_CONTRACT_IDS(
  db: string,
  env: Environment,
  contractIds: number[],
  termTypes: string[],
): string {
  if (contractIds.length < 1) {
    throw new RangeError("Must filter by at least 1 contract ID");
  }
  if (termTypes.length < 1) {
    throw new RangeError("Must filter by at least 1 term type");
  }
  return `
        SELECT
            attachments AS "attachments",
            contract_id AS "contractId",
            contract_term_id AS "id",
            term_type AS "type"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.contract_term 
        WHERE 
            deleted_by IS NULL 
            AND deleted_at IS NULL
            AND term_type IN ('${termTypes.join("','")}')
            AND contract_id IN (${contractIds.join(",")})
    ;`;
}

export function GET_CURRENT_STATEMENT_PERIOD_IDS_BY_STATEMENT_PERIOD_IDS(
  db: string,
  env: Environment,
  statementPeriodIds: number[],
): string {
  if (statementPeriodIds.length < 1) {
    throw new RangeError("Must filter by at least 1 statement period ID");
  }
  return `
        SELECT
            statement_period_id AS "id"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.statement_period 
        WHERE 
            closed_date IS NULL 
            AND statement_period_status = 'current'
            AND statement_period_id IN (${statementPeriodIds.join(",")})
    ;`;
}

export function GET_ABACUS_EVENTS(
  db: string,
  env: Environment,
  targetIds: number[],
  eventName?: string,
  targetType?: string,
  createdBy?: string,
): string {
  if (targetIds.length < 1) {
    throw new Error("Must filter by at least one target ID");
  }
  const filters: string[] = ["1 = 1"];
  if (createdBy != null) {
    filters.push(`created_by = '${createdBy}'`);
  }
  if (eventName != null) {
    filters.push(`event_name = '${eventName}'`);
  }
  if (targetType != null) {
    filters.push(`target_type = '${targetType}'`);
  }
  return `
    SELECT 
      ABACUS_EVENT_ID,
      CREATED_BY,
      EVENT_DATE,
      EVENT_NAME,
      PREVIOUS_ABACUS_EVENT_ID,
      ROLLED_BACK_AT,
      STATEMENT_PERIOD_ID,
      TARGET_ID,
      TARGET_TYPE
    FROM ${db}.${env}_ROYALTY_ACCOUNTING_ROYALTY_ACCOUNTING.abacus_event
    WHERE   ${filters.join(" AND ")}
            AND target_id IN (${targetIds.join(",")})
  `;
}

export function GET_ABACUS_STATES(
  db: string,
  schema: string,
  parentTableName: string,
  parentTableIds: number[],
  actionName?: string,
  actionStatus?: AbacusStateActionStatus,
): string {
  if (parentTableIds.length < 1) {
    throw new Error("Must filter by at least one parent table ID");
  }
  const filters: string[] = [];
  if (actionName != null) {
    filters.push(`AND action_name = '${actionName}'`);
  }
  if (actionStatus != null) {
    filters.push(`AND action_status = '${actionStatus}'`);
  }
  return `
    SELECT
      ABACUS_STATE_ID AS "abacus_state_id",
      ACTION_NAME AS "action_name",
      ACTION_STATUS AS "action_status",
      CREATED_AT AS "created_at",
      CREATED_BY AS "created_by",
      LAST_MODIFIED AS "last_modified",
      LAST_MODIFIED_BY AS "last_modified_by",
      MESSAGE AS "message",
      PARENT_TABLE_ID AS "parent_table_id",
      PARENT_TABLE_NAME AS "parent_table_name"
    FROM ${db}.${schema}.abacus_state
    WHERE 
      parent_table_name = '${parentTableName}'
      ${filters.join(" ")}
      AND parent_table_id IN (${parentTableIds.join(",")})
  ;`;
}

export function GET_STATEMENT_PERIODS_BY_YEARS(
  db: string,
  env: Environment,
  years: number[],
): string {
  if (years.length < 1) {
    throw new RangeError("Must filter by at least 1 year");
  }
  return `
        SELECT
            closed_by AS "closedBy",
            closed_date AS "closedDate",
            statement_period_id AS "id",
            statement_month AS "month",
            statement_period_name AS "name",
            statement_period_status AS "status",
            statement_year AS "year"
        FROM 
            ${db}.${env}_royalty_accounting_royalty_accounting.statement_period 
        WHERE 
            statement_year IN (${years.join(",")})
    ;`;
}

export function GET_UPCS_BY_DISPLAY_UPCS(
  db: string,
  displayUpcs: string[],
): string {
  if (displayUpcs.length < 1) {
    throw new RangeError("Must filter by at least 1 display UPC");
  }
  return `
        SELECT
            display_upc AS "displayUpc",
            upc AS "upc"
        FROM 
            ${db}.art_relations_prod_art_relations.releases 
        WHERE 
            TO_VARCHAR(upc) != display_upc
            AND display_upc IN ('${displayUpcs.join("','")}')
    ;`;
}

export function GET_UPCS_BY_UPCS_AND_VENDOR_IDS(
  db: string,
  vendorIds: number[],
  upcs: string[],
): string {
  if (vendorIds.length < 1) {
    throw new RangeError("Must filter by at least 1 label ID");
  }
  if (upcs.length < 1) {
    throw new RangeError("Must filter by at least 1 upc");
  }
  return `
        SELECT 
            r.display_upc AS "displayUpc",
            r.upc AS "upc",
            v.vendor_id AS "vendorId"
        FROM 
            ${db}.art_relations_prod_art_relations.releases AS r
            INNER JOIN ${db}.art_relations_prod_art_relations.project AS p
                USING (project_id)
            INNER JOIN ${db}.art_relations_prod_art_relations.vendor AS v
                USING (vendor_id)
        WHERE 
            v.vendor_id IN (${vendorIds.join(",")})
            AND (
                r.upc IN (${upcs.join(",")})
                OR r.display_upc IN ('${upcs.join("','")}')
            )
    ;`;
}
